import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';

import { api, type ReferralStats } from '../api/client';
import { T } from '../styles/tokens';
import { haptic, hapticSuccess, tg } from '../telegram/sdk';

interface Props {
  onBack: () => void;
}

export default function Referral({ onBack }: Props) {
  const [data, setData] = useState<ReferralStats | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.referral.get().then(setData).catch((e) => setErr(e instanceof Error ? e.message : 'Не удалось загрузить'));
  }, []);

  const copy = async () => {
    if (!data) return;
    haptic('light');
    try {
      await navigator.clipboard.writeText(data.link);
      hapticSuccess();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setErr('Не удалось скопировать — выдели вручную');
    }
  };

  const share = () => {
    if (!data) return;
    haptic('medium');
    const text = encodeURIComponent('Держи VPN Saler — быстрый VPN с обходом блокировок.');
    const url = encodeURIComponent(data.link);
    tg()?.openLink(`https://t.me/share/url?url=${url}&text=${text}`);
  };

  return (
    <div style={r.wrap}>
      <div style={r.header}>
        <button style={r.backBtn} onClick={() => { haptic('light'); onBack(); }}>← Назад</button>
        <div style={r.headTag}>// бонусы</div>
        <div style={r.headTitle}>Рефералы</div>
      </div>

      <div style={r.bonusRow}>
        <div style={r.bonusCard}>
          <div style={r.bonusWho}>Вы</div>
          <div style={r.bonusVal}>+7 дней</div>
        </div>
        <div style={r.bonusCard}>
          <div style={r.bonusWho}>Друг</div>
          <div style={r.bonusVal}>+7 дней</div>
        </div>
      </div>

      <div style={r.linkCard}>
        <div style={r.label}>// ваша ссылка</div>
        <code style={r.linkCode}>{data?.link || (err ? '—' : 'Загрузка...')}</code>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button style={{ ...r.cta, background: copied ? T.success : T.accent }} onClick={copy} disabled={!data}>
            {copied ? '✓ Скопировано' : 'Копировать'}
          </button>
          <button style={r.ctaSec} onClick={share} disabled={!data}>
            Поделиться
          </button>
        </div>
      </div>

      <div style={r.statsRow}>
        {[
          [String(data?.invited_count ?? 0), 'Приглашено'],
          [String(data?.paid_count ?? 0), 'Оплатили'],
          [`${data?.bonus_days_received ?? 0} дн.`, 'Бонусов'],
        ].map(([v, l]) => (
          <div key={l} style={r.statItem}>
            <div style={r.statVal}>{v}</div>
            <div style={r.statLabel}>{l}</div>
          </div>
        ))}
      </div>

      {err && <div style={r.err}>⚠ {err}</div>}

      <div style={r.rules}>
        <div style={r.label}>// как это работает</div>
        <ol style={r.ol}>
          <li>Отправьте ссылку другу.</li>
          <li>Друг переходит по ней и запускает бота.</li>
          <li>После его первой оплаты вы оба получите +7 дней.</li>
        </ol>
      </div>
    </div>
  );
}

const r: Record<string, CSSProperties> = {
  wrap: { padding: '16px 16px 24px' },
  header: { marginBottom: 18 },
  backBtn: { background: 'none', border: 'none', color: T.accent, fontFamily: 'DM Mono, monospace', fontSize: 12, padding: 0, cursor: 'pointer', marginBottom: 10 },
  headTag: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.accent, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 },
  headTitle: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 24, letterSpacing: '-0.03em' },
  bonusRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 },
  bonusCard: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 4, padding: '14px 16px' },
  bonusWho: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 },
  bonusVal: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 20, color: T.accent, letterSpacing: '-0.02em' },
  linkCard: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 4, padding: '14px 16px', marginBottom: 14 },
  label: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 },
  linkCode: { display: 'block', fontFamily: 'DM Mono, monospace', fontSize: 12, color: T.text, padding: '10px 12px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 3, wordBreak: 'break-all' as const },
  cta: { flex: 1, border: 'none', borderRadius: 3, padding: '10px 12px', color: '#000', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  ctaSec: { flex: 1, background: T.surfaceUp, border: `1px solid ${T.border}`, borderRadius: 3, padding: '10px 12px', color: T.text, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', border: `1px solid ${T.border}`, borderRadius: 4, overflow: 'hidden', marginBottom: 14 },
  statItem: { padding: '12px 10px', borderRight: `1px solid ${T.border}`, background: T.surface, textAlign: 'center' as const },
  statVal: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 20, color: T.text, letterSpacing: '-0.02em', marginBottom: 3 },
  statLabel: { fontFamily: 'DM Mono, monospace', fontSize: 9, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase' as const },
  err: { padding: '10px 12px', border: '1px solid oklch(0.58 0.19 27 / 0.5)', background: 'oklch(0.58 0.19 27 / 0.08)', color: 'oklch(0.72 0.19 27)', fontFamily: 'DM Mono, monospace', fontSize: 11, borderRadius: 3, marginBottom: 14 },
  rules: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 4, padding: '14px 16px' },
  ol: { margin: 0, paddingLeft: 18, color: T.textSec, fontFamily: 'DM Sans, sans-serif', fontSize: 12, lineHeight: 1.7 },
};
